import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actions as accountsActions } from "reducers/accounts";
import { isDesktop } from "react-device-detect";
import * as configUtils from "util/config";
import * as i18n from "util/i18n";

export const types = { CA: "savings", CC: "checking", CBB: "CBB" };

export const useGetAccounts = (
    currency = "",
    accountType = "",
    scopeToShow = "",
    refreshing = false,
    short = false,
) => {
    const dispatch = useDispatch();
    const [accountOptions, setAccountOptions] = useState([]);
    const { accounts, fetching } = useSelector((state) => state.accounts);
    const isBackoffice = window.self !== window.top;

    const formatLabel = (account) => {
        const accountName =
            short || !isDesktop ? account?.accountType : i18n.get(`productType.${account?.accountType}`);
        return `${accountName} ····${account?.accountNumber?.slice(-4)}`;
    };

    const formatAccountSelector = (account) => ({
        id: account?.productId,
        label: formatLabel(account),
        balance: { currency: account?.currency, quantity: account?.availableBalance },
    });

    const handleSelectorOptions = () => {
        let _accounts = accounts;
        if (accountType || currency) {
            _accounts = accounts.filter((account) => {
                if (accountType && currency) {
                    return account.currency === currency && account.accountType === accountType;
                }
                return account.currency === currency || account.accountType === accountType;
            });
        }
        setAccountOptions(_accounts.map((account) => formatAccountSelector(account)));
    };

    useEffect(() => {
        if (isBackoffice) {
            return;
        }

        const bibankAccEnabled = configUtils.getBoolean("bibank.accounts.type.enabled");

        if (accounts.length === 0 || refreshing) {
            if (bibankAccEnabled && scopeToShow === "internalTransfers") {
                dispatch(accountsActions.listAllAccounts());
            } else if (fetching === false) {
                // se valida fetching false para evitar invocaciones multiples al activity
                dispatch(accountsActions.listAccounts());
            }
        }
    }, [dispatch]);

    useEffect(() => {
        if (accounts.length !== 0) {
            handleSelectorOptions();
        }
    }, [accounts]);

    return { accounts, formatAccountSelector, formatLabel, accountOptions, dispatch, fetching };
};

export const useGetAccount = (idAccount) => {
    const { accounts } = useGetAccounts();
    return accounts.find((account) => account.productId === idAccount);
};

export const useAccount = (key, value) => {
    const dispatch = useDispatch();
    const [account, setAccount] = useState({});
    const { accounts, selectedAccount, fetching } = useSelector((state) => state.accounts);
    const isBackoffice = window.self !== window.top;
    useGetAccounts();

    const formatAccountBody = (_account) =>
        `····${_account?.accountNumber?.slice(-4)} ${_account?.accountType} ${i18n.get(
            `currency.label.${_account?.currency}`,
        )}`;

    useEffect(() => {
        if (isBackoffice) {
            return;
        }
        if (value) {
            dispatch(accountsActions.readAccount(value));
        }
    }, [value]);

    useEffect(() => {
        if (accounts.length !== 0 && value) {
            const selected = accounts.find((item) => item[key] === value);
            setAccount(selected);
        }
    }, [accounts, value]);

    return { account, selectedAccount, fetching, formatAccountBody, dispatch };
};

export const useAccountSelector = (currency, accountType) => {
    const { accounts, fetching } = useSelector((state) => state.accounts);
    const [accountOptions, setAccountOptions] = useState([]);
    useGetAccounts();

    const formatSelectorLabel = (account) =>
        `${i18n.get(`accounts.productType.${types[account.accountType]}`)} Nº ····${account.accountNumber?.slice(-4)}`;

    const handleSelectorOptions = () => {
        const options = accounts.filter(
            (account) => account.currency === currency && account.accountType === accountType,
        );
        setAccountOptions(
            options.map((account) => ({
                value: account.productId,
                label: formatSelectorLabel(account),
                currency: i18n.get(`account.agreement.currency.${account.currency}`),
            })),
        );
    };

    useEffect(() => {
        if (accounts.length !== 0) {
            handleSelectorOptions();
        }
    }, [accounts]);

    return {
        accountOptions,
        accounts,
        fetching,
    };
};

export default useAccountSelector;
